import type { Resident } from '@lmc/ts-common'
import { postJSON } from '../../../../common/services/api'
import { getResidentPronouns } from '../../../../common/utils'

export type FormState = {
  ability: boolean
  capacity: boolean
  name: string
  relationship: string
  imageURL: string
  hasConfirmed: boolean
  lpaValue: any
  comment: string
  reason: string
}

export const generateLogText = (
  formState: FormState,
  resident: Resident,
  carePlanName: string,
  preferredCarePlanName: string
) => {
  const { capacity, ability, comment, reason, name, relationship } = formState

  const residentName = resident?.preferredName || resident.name.first
  const { subject: pronoun, verb } = getResidentPronouns(resident)

  let title: string
  let description: string

  if (capacity) {
    if (ability) {
      title = `Signed by ${residentName}`
      description = `${residentName} confirms that ${pronoun} ${verb('approve')} the ${carePlanName} ${preferredCarePlanName}.`
    } else {
      title = `Signed by ${name}`
      description = `${residentName} confirms that ${pronoun} ${verb('approve')} the ${carePlanName} ${preferredCarePlanName}. Witnessed and signed by ${name} (${relationship}).`
    }
  } else {
    title = `Signed by ${name}`
    description = `${name} (${relationship}) signed the ${carePlanName} ${preferredCarePlanName} on behalf of ${residentName}.`
  }

  if (reason) description += ` ${residentName} did not sign because: ${reason}.`
  if (comment) description += ` ${comment}.`

  return { title, description }
}

// @ts-ignore
export const onSubmit = async ({
  formState,
  setLoadedListItem,
  carePlan,
  resident,
  onSuccess,
  onError,
  preferredCarePlanName,
  carePlanName,
  category = '5e867702b349f776c8d48c2f',
  item = '61794b0a988b150f6ebdac37',
  queryClient
}: any) => {
  try {
    const { title, description } = generateLogText(
      formState,
      resident,
      carePlanName,
      preferredCarePlanName
    )
    const { imageURL } = formState
    const log = {
      resident: resident.id,
      title,
      description,
      carePlanId: carePlan.carePlanId, // should be the permanent ID, not the objectID
      hidden: true,
      isCustomEntry: true,
      category,
      item
    }

    const res = await postJSON({
      url: 'list/logs',
      json: log
    })

    // The Create Log endpoint can't handle pictures
    // So we need to use the PUT request to upload a photo
    // TODO: refactor and merge the requests!
    // eslint-disable-next-line prefer-const
    const form = new FormData()
    const file = dataURItoBlob(imageURL)
    // @ts-ignore
    form.append('picture1', file)

    const updatedLog = await postJSON({
      url: `list/logs/${res.result.id}`,
      method: 'PUT',
      contentType: 'multipart/form-data',
      body: form // log,
    })

    setLoadedListItem('logs', updatedLog.result)
    await postJSON({
      url: `list/care-plans/${carePlan.id}/sign`,
      json: { id: res.result.id }
    })
    queryClient?.invalidateQueries(['care-plans', carePlan.id, 'populated'])
    onSuccess()
  } catch (e) {
    onError(e)
  }
}

export function dataURItoBlob(dataURI: any) {
  try {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString
    if (dataURI.split(',')[0].indexOf('base64') >= 0)
      byteString = atob(dataURI.split(',')[1])
    else byteString = unescape(dataURI.split(',')[1])

    // separate out the mime component
    // const mimeString = dataURI.split(',')[0].split(':')[1].split('')[0]

    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length)
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    // const file = new Blob([ia], { type: mimeString })
    // const uri = URL.createObjectURL(file)
    // return {
    //     uri,
    //     type: 'image/png',
    //     name: `signature.png`
    // }
    return new Blob([ia], { type: 'image/png' })
  } catch (e) {
    return null
  }
}
